import logo from '@repo/assets/images/logo.png';
import { useLocation } from 'react-router-dom';
import { useTranslation, isRTL } from '@repo/settings';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

export interface NavItem {
  path?: string;
  labelKey: string;
  icon: LucideIcon;
  items?: NavItem[];
  /** Optional badge: shown as a pill counter on the nav item */
  badge?: number;
}

export interface AppSidebarProps {
  navItems: NavItem[];
}

export function AppSidebar({ navItems }: AppSidebarProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const sidebarSide = isRTL(i18n.language) ? 'right' : 'left';
  const { isMobile, setOpenMobile } = useSidebar();

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="offcanvas" side={sidebarSide}>
      {/* Header with Logo */}
      <SidebarHeader className="h-20 flex-row items-center border-b border-sidebar-border px-4">
        <img src={logo} className="size-12 rounded-sm shadow-sm" alt="Logo" />
        <div className={`${isRTL(i18n.language) ? 'text-right' : 'text-left'} flex-1 min-w-0`}>
          <h1 className="text-xl font-bold text-sidebar-foreground leading-tight">
            {t('app.name')}
          </h1>
          <p className="text-sidebar-foreground/70 text-sm truncate">{t('app.subtitle')}</p>
        </div>
      </SidebarHeader>

      {/* Navigation Menu */}
      <SidebarContent className="p-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-sidebar-border/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-border/70 transition-colors">
        <SidebarGroup className="p-0">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-2">
              {navItems.map((item) => {
                const isActive = item.path ? location.pathname === item.path : false;

                if (item.items && item.items.length > 0) {
                  const isChildActive = item.items.some(
                    (child) => child.path && location.pathname === child.path
                  );

                  return (
                    <Collapsible
                      key={item.labelKey}
                      asChild
                      defaultOpen={isChildActive}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <div
                            className={`
                                flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium border transition-all w-full cursor-pointer select-none
                                ${
                                  isChildActive
                                    ? 'bg-primary/15 text-primary border-primary/30 shadow-sm'
                                    : item.badge && item.badge > 0
                                      ? 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 border-rose-200 dark:border-rose-800/50 bg-rose-50/50 dark:bg-rose-900/10 shadow-sm'
                                      : 'text-muted-foreground hover:bg-accent hover:text-foreground border-transparent hover:shadow-sm'
                                }
                              `}
                          >
                            <item.icon
                              size={22}
                              className={`transition-colors ${
                                isChildActive
                                  ? 'text-primary'
                                  : item.badge && item.badge > 0
                                    ? 'text-rose-500'
                                    : 'text-muted-foreground'
                              }`}
                            />
                            <span className="flex-1 text-start">{t(item.labelKey)}</span>
                            {/* Badge counter */}
                            {item.badge && item.badge > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-black px-1.5 shadow-sm animate-pulse">
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            ) : (
                              <ChevronRight
                                size={16}
                                className={`transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:rotate-90 ${
                                  isChildActive ? 'text-primary' : 'text-muted-foreground'
                                }`}
                              />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub className="me-0 pe-0 rtl:border-l-0 rtl:border-r border-sidebar-border/70 ms-5 ps-0 space-y-1 mt-1 transition-all">
                            {item.items.map((subItem) => {
                              const isSubActive = subItem.path
                                ? location.pathname === subItem.path
                                : false;
                              return (
                                <SidebarMenuSubItem key={subItem.labelKey} className="px-0">
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={isSubActive}
                                    className={`
                                      flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all w-full mb-0.5 h-auto border
                                      ${
                                        isSubActive
                                          ? 'bg-blue-50 !text-primary dark:bg-blue-950/30 dark:text-blue-400 font-medium border-blue-100 dark:border-blue-900/30 shadow-sm'
                                          : subItem.badge && subItem.badge > 0
                                            ? 'text-rose-600 dark:text-rose-400 bg-rose-50/60 dark:bg-rose-900/10 border-rose-100 dark:border-rose-900/30 font-medium'
                                            : 'text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground font-normal border-transparent'
                                      }
                                    `}
                                  >
                                    <NavLink to={subItem.path || '#'} onClick={handleLinkClick}>
                                      <subItem.icon
                                        size={18}
                                        className={`transition-colors ${
                                          isSubActive
                                            ? '!text-blue-900 !dark:text-blue-400'
                                            : subItem.badge && subItem.badge > 0
                                              ? 'text-rose-500'
                                              : 'text-muted-foreground/40'
                                        }`}
                                      />
                                      <span className="flex-1">{t(subItem.labelKey)}</span>
                                      {subItem.badge && subItem.badge > 0 ? (
                                        <span className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-black px-1.5">
                                          {subItem.badge > 99 ? '99+' : subItem.badge}
                                        </span>
                                      ) : isSubActive ? (
                                        <div className="size-1 rounded-full !bg-primary dark:bg-blue-400" />
                                      ) : null}
                                    </NavLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                return (
                  <SidebarMenuItem key={item.path || item.labelKey}>
                    <NavLink
                      to={item.path || '#'}
                      onClick={handleLinkClick}
                      className={`
                        flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium border transition-all
                        ${
                          isActive
                            ? 'bg-primary/15 text-primary border-primary/30 shadow-sm'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground border-transparent hover:shadow-sm'
                        }
                      `}
                    >
                      <item.icon
                        size={22}
                        className={isActive ? 'text-primary' : 'text-muted-foreground'}
                      />
                      <span className="flex-1 text-start">{t(item.labelKey)}</span>
                      {isActive && (
                        <ChevronRight
                          size={16}
                          className="ms-auto text-primary/60 rtl:rotate-180"
                        />
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
